<template>
  <ReportDetailsHeader :report="report" :back-route="backRoute" @export="showExportDialog = true" />

  <div class="report-details-container">
    <ReportSectionContent :report="report" :active-tab="activeTab" @update:active-tab="activeTab = $event" />

    <ReportChat v-if="!isAdmin() && isDesktop" />
  </div>

  <!-- Mobile: floating chat button -->
  <AppButton
    v-if="!isAdmin() && !isDesktop"
    variant="primary"
    class="chat-fab"
    @click="isChatOverlayOpen = true"
  >
    <AppIcon color="white" type="chat" size="28" />
  </AppButton>

  <!-- Mobile: full-screen chat overlay -->
  <Transition name="chat-overlay">
    <div
      v-if="!isAdmin() && !isDesktop && isChatOverlayOpen"
      class="chat-overlay"
    >
      <ReportChat overlay @close="isChatOverlayOpen = false" />
    </div>
  </Transition>

  <ReportExportDialog v-model="showExportDialog" />
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { AppButton, AppIcon } from "@/components/ui";
import { isAdmin } from "@/utilities/token";
import { useReportDetails } from "@/composables/useReportDetails";
import ReportDetailsHeader from "@/views/ReportDetailsView/components/ReportDetailsHeader.vue";
import ReportSectionContent from "@/views/ReportDetailsView/components/ReportSectionContent.vue";
import ReportChat from "@/views/ReportDetailsView/components/ReportChat/ReportChat.vue";
import ReportExportDialog from "@/views/ReportDetailsView/components/ReportExportDialog.vue";

const DESKTOP_BREAKPOINT = 1024;

const route = useRoute();
const reportId = route.params.id;

const { report, activeTab, showExportDialog, fetchReport } = useReportDetails(reportId);

const isDesktop = ref(window.innerWidth > DESKTOP_BREAKPOINT);
const isChatOverlayOpen = ref(false);

const handleResize = () => {
  isDesktop.value = window.innerWidth > DESKTOP_BREAKPOINT;
  if (isDesktop.value) {
    isChatOverlayOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  fetchReport();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  document.body.style.overflow = "";
});

watch(isChatOverlayOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});

const backRoute = computed(() => {
  return window.history.state.backRoute || { name: isAdmin() ? "manage-users" : "reports" };
});
</script>

<style lang="scss" scoped>
.report-details-container {
  display: flex;
  gap: 30px;
  padding: 30px;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 10px 10px 88px 10px; // chat button height 40px + 2*24px absolute position
  }
}

.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999;
  width: 56px;
  height: 56px;
  padding: 0;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.chat-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-overlay-enter-active,
.chat-overlay-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.chat-overlay-enter-from,
.chat-overlay-leave-to {
  transform: translateX(20%);
  opacity: 0;
}

.chat-overlay-enter-to,
.chat-overlay-leave-from {
  transform: translateX(0);
  opacity: 1;
}
</style>
